import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import moment from 'moment'
import { DataTable } from '@components/v2/dataTable'
import { getTemplateErrorRate } from '@global/api'
import { cn } from '@global/utils/tailwind'
import { getTemplatesQuery } from 'pages/datasets/api'
import { Dataset } from 'pages/datasets/types'
import { Tooltip } from '@components/tooltip/tooltip'
import { InformationCircle } from './informationCircle'
import { TOOLTIP_INFO } from '../constants'
import { DEFAULT_ERROR_REPORTS_QUERY_PARAMS } from '../queries/useReportsErrorRateQuery'
import { t } from '@lingui/core/macro'

interface ITemplate extends Dataset {
  count: number
  compliance: number
}

const SIX_MONTHS_AGO = moment().subtract(5, 'months').startOf('month').valueOf() // timestamp in milliseconds

export const AllTemplatesCard = () => {
  const COLUMNS = useMemo(
    () => [
      {
        label: 'Name',
        key: 'name',
        sortable: true,
        columnWidth: '300px',
      },
      {
        label: 'Last Submitted',
        key: 'lastSubmissionTime',
        sortable: true,
        defaultSortable: true,
        columnWidth: '160px',
        type: 'date',
      },
      {
        label: 'Rows',
        key: 'count',
        sortable: true,
        columnWidth: '140px',
        type: 'number',
      },
      {
        label: 'Score',
        key: 'compliance',
        sortable: true,
        type: 'number',
      },
      {
        label: 'Integrations',
        key: 'integrations',
      },
    ],
    [],
  )

  const { data: templateErrorRate, isLoading: errorRateLoading } = useQuery({
    queryKey: [
      'roi-template-error-rate',
      DEFAULT_ERROR_REPORTS_QUERY_PARAMS.numberOfDaysToFilter,
      DEFAULT_ERROR_REPORTS_QUERY_PARAMS.isFilteredByRow,
    ],
    queryFn: ({ signal }) =>
      getTemplateErrorRate({ signal, ...DEFAULT_ERROR_REPORTS_QUERY_PARAMS }),
    refetchOnWindowFocus: false,
  })

  const { data: templateData, isLoading } = useQuery({
    ...getTemplatesQuery,
    refetchOnWindowFocus: false,
    enabled: Boolean(templateErrorRate),
    select: data => {
      if (!templateErrorRate?.length) {
        return data.filter(
          template =>
            template.lastSubmissionTime &&
            Date.parse(template.lastSubmissionTime) >= SIX_MONTHS_AGO,
        )
      }

      const errorRateMap = new Map(templateErrorRate.map(errRate => [errRate.id, errRate]))

      return data.reduce<ITemplate[]>((acc: ITemplate[], template: Dataset) => {
        if (!template.lastSubmissionTime) return acc

        const lastSubmittedTime = Date.parse(template.lastSubmissionTime)
        if (lastSubmittedTime < SIX_MONTHS_AGO) return acc

        const errorRate = errorRateMap.get(template.id)
        if (!errorRate) return acc

        acc.push({
          ...template,
          count: errorRate?.total_rows || errorRate.total_submissions || 0,
          compliance: Math.trunc(
            100 -
              (100 * +errorRate.error_count) /
                +(errorRate?.total_rows || errorRate?.total_submissions || 0),
          ),
        })

        return acc
      }, [])
    },
  })

  return (
    <section className="flex flex-col gap-[20px]">
      <div className="flex items-center gap-3 ml-4">
        <h1 className="text-cv-blue-700 text-base font-bold">{t`All-template compliance snapshot`}</h1>
        <Tooltip
          title={<p className="text-sm font-bold">{TOOLTIP_INFO.TEMPLATES.TITLE}</p>}
          description={<span className="text-xs">{TOOLTIP_INFO.TEMPLATES.DESCRIPTION}</span>}
          mode="dark"
          className="w-[383px] p-[16px]"
        >
          <InformationCircle />
        </Tooltip>
      </div>
      <DataTable
        isLoading={isLoading || errorRateLoading}
        columns={COLUMNS}
        data={templateData}
        itemContent={(index, item) => (
          <tr className="border border-cv-blue-200 bg-white" key={index}>
            {COLUMNS.map(({ key }) => {
              let content

              switch (key) {
                case 'integrations':
                  content = (
                    <div className="flex">
                      {item[key]
                        .map((integration, idx) => {
                          // exclude email from integrations list since it's a default common integration.
                          if (integration === 'email-output') return null

                          return (
                            <img
                              key={`${index}-${key}-${idx}`}
                              style={{ maxWidth: 18 }}
                              className="mr-2"
                              src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${integration}.png`}
                            />
                          )
                        })
                        .filter(v => v)}
                    </div>
                  )
                  break
                case 'lastSubmissionTime':
                  content = new Date(item[key]).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                  break
                case 'submissionCount':
                  content = item.submissionCount || 0
                  break
                case 'compliance':
                  content = (
                    <div
                      className={cn(
                        'flex items-center font-extrabold leading-[21px]',
                        item.compliance < 80
                          ? 'text-[#E41B0C]'
                          : item.compliance < 90
                            ? ' text-[#F3B03E]'
                            : 'text-[#4AA97E]',
                      )}
                    >
                      <ShieldCheck
                        size={21}
                        className="mr-1"
                        color={
                          item.compliance < 80
                            ? '#E41B0C'
                            : item.compliance < 90
                              ? ' #F3B03E'
                              : '#4AA97E'
                        }
                      />
                      {item.compliance}%
                    </div>
                  )
                  break
                default:
                  content = item[key]
                  break
              }

              return (
                <td
                  className="p-4 text-sm relative first:pl-[20px]"
                  key={`${index}-${key}`}
                  data-tooltip={content?.length >= 45 ? content : null}
                  data-direction="bottom"
                >
                  {key === 'name' ? (
                    <a
                      className="overflow-hidden whitespace-nowrap text-ellipsis max-w-[45ch] block underline"
                      href={`/template/${item.id}/data`}
                      title={`Go to template ${item.name} dataset view`}
                    >
                      {content}
                    </a>
                  ) : (
                    <span className="overflow-hidden whitespace-nowrap text-ellipsis max-w-[45ch] block">
                      {content}
                    </span>
                  )}
                </td>
              )
            })}
          </tr>
        )}
      />
    </section>
  )
}
